import React, { useMemo, useState } from "react";
import { StyleSheet, View } from "react-native";
import {
  IOfferItem,
  isOverHeartMealsProductsSelector,
  isOverProductsLimitSelector,
  OfferViewModel,
} from "@modules";
import { ProductItem, IImportationTypes, OfferFormulaItem } from "@atomic";
import { TestIDs } from "@utils";
import { useSelector } from "react-redux";
import { GlobalState } from "@redux";
import { useDevices } from "@hooks";
import { ArticleFamily, OrderItem } from "@foodi/core";

interface IProps {
  familyItems: IOfferItem[];
  selectedOfferId?: string;
  selectedActiveOfferId?: string;
  familyName: string;
  updateOrders: Function;
  sameProductPerOrder: number;
  isHeartFamily: boolean;
  isBookingSelected?: boolean;
  selectedDay?: number;
  orderItems?: OrderItem[];
  menuType?: IImportationTypes;
}

const areEqual = (prevProps: IProps, nextProps: IProps) =>
  nextProps.isBookingSelected
    ? JSON.stringify(prevProps.familyItems) ===
      JSON.stringify(nextProps.familyItems)
    : prevProps.selectedActiveOfferId === nextProps.selectedActiveOfferId &&
      prevProps.selectedDay === nextProps.selectedDay &&
      prevProps.sameProductPerOrder === nextProps.sameProductPerOrder;

export const ProductsFamily: React.FC<IProps> = React.memo(
  ({
    isHeartFamily,
    familyItems,
    selectedOfferId,
    familyName,
    updateOrders,
    sameProductPerOrder,
    isBookingSelected,
    orderItems,
    menuType,
  }) => {

    const [offerVM] = useState(new OfferViewModel());
    familyItems = offerVM.sortFamilyItemsByName(familyItems);

    const [isMobile] = useDevices();
    const styles = useMemo(() => _styles(isMobile), [isMobile]);

    const outOfStockItems = useSelector(
      (state: GlobalState) => state.offers?.outOfStockItems
    );
    const outOfStockItemsFormula = useSelector(
      (state: GlobalState) => state.offers?.outOfStockItemsFormula
    );

    const isOverProductsLimit = useSelector(isOverProductsLimitSelector);
    const isOverHeartMealsProducts = useSelector(
      isOverHeartMealsProductsSelector
    );
    const isMenu = menuType !== undefined;

    return (
      <View style={styles.groupList}>
        {familyItems.map((item: IOfferItem | OfferFormulaItem, index) => {
          const outOfStockFormulaSubItemsIds = outOfStockItemsFormula?.filter(i =>  i.idOfferItemFormulaParent === item?.id).map((i) => i.idOfferItemFormula)

          const eachStepHasOneProductWithStock = (item as OfferFormulaItem)?.steps?.every((step: any, index: number) =>
              (item as OfferFormulaItem)?.subProducts?.some(
                    (subProduct: any) => subProduct.stepNumber === index && subProduct.quantityRemaining > 0 && !outOfStockFormulaSubItemsIds.includes(subProduct.id)
                )
            );

          const outOfStockItem = outOfStockItems?.some(
            (o) => o?.idOfferItem === item?.id || o?.id === item?.id
          );

          const orderItem = orderItems?.find?.(
            (o: OrderItem) => o?.id === item?.id
          );

          const isOrderable = item.inheritedFamily !== ArticleFamily.FORMULA || item?.isFormulaComplete
          const isOutOfStock = item.inheritedFamily !== ArticleFamily.FORMULA ? outOfStockItem : !eachStepHasOneProductWithStock || outOfStockItem

          return (
            (isMenu || item.quantityRemaining > 0 && isOrderable) && (
              <ProductItem
                forwardTestID={`${TestIDs.restaurantDetail.views.listProductItem}${familyName}_${index}`}
                key={item.id}
                item={item}
                lastProduct={index + 1 === familyItems.length}
                selectedOfferId={selectedOfferId}
                updateOrders={updateOrders}
                outOfStockItem={isOutOfStock}
                sameProductPerOrder={sameProductPerOrder}
                isOverProductsLimit={
                  isOverProductsLimit ||
                  (isHeartFamily && isOverHeartMealsProducts)
                }
                hasQuantity={!isBookingSelected}
                orderItem={orderItem}
                menuType={menuType}
              />
            )
          );
        })}
      </View>
    );
  },
  areEqual
);

const _styles = (isMobile: boolean) =>
  StyleSheet.create({
    groupList: {
      flexDirection: isMobile ? "column" : "row",
      justifyContent: isMobile ? "center" : "space-between",
      width: isMobile ? undefined : 771 ,
      flexWrap: "wrap",
    }
  });
